/**
 * SignalR Service
 * Manages real-time connection to the notifications hub
 */
import * as signalR from '@microsoft/signalr';
import type { SignalRNotification, ProgressUpdateDto } from '@/types/signalr-notifications';
import { createLogger } from '@/utils/logger';

const log = createLogger('SignalRService');

export type NotificationHandler = (notification: SignalRNotification) => void;
export type ProgressUpdateHandler = (update: ProgressUpdateDto) => void;

class SignalRService {
  private static instance: SignalRService;
  private connection: signalR.HubConnection | null = null;
  private handlers: Set<NotificationHandler> = new Set();
  private progressHandlers: Set<ProgressUpdateHandler> = new Set();
  private reconnectAttempts = 0;
  private maxReconnectAttempts = 5;
  private isConnecting = false;
  private currentToken: string | null = null; // Track current token to detect changes

  private constructor() {}

  public static getInstance(): SignalRService {
    if (!SignalRService.instance) {
      SignalRService.instance = new SignalRService();
    }
    return SignalRService.instance;
  }

  /**
   * Initialize SignalR connection
   */
  public async connect(accessToken: string): Promise<void> {
    // Detect if token has changed and force reconnection
    const tokenChanged = this.currentToken && this.currentToken !== accessToken;

    if (tokenChanged) {
      log.debug('Token changed detected, forcing reconnection...');
      await this.disconnect();
    }

    if (this.connection?.state === signalR.HubConnectionState.Connected || this.isConnecting) {
      log.debug('Already connected or connecting, skipping...');
      return;
    }

    this.isConnecting = true;
    this.currentToken = accessToken; // Store token for change detection

    try {
      const apiUrl = import.meta.env.VITE_API_URL || '';
      const hubUrl = `${apiUrl}/hubs/notifications`;

      log.debug('Connecting to', hubUrl);
      log.debug('Token available:', !!accessToken);

      // TEMPORARY WORKAROUND: Use query string for token to avoid CORS issues
      // This should be removed once backend CORS is properly configured
      const hubUrlWithToken = `${hubUrl}?access_token=${encodeURIComponent(accessToken)}`;

      this.connection = new signalR.HubConnectionBuilder()
        .withUrl(hubUrlWithToken, {
          // Comment out accessTokenFactory to use query string instead
          // accessTokenFactory: () => {
          //   console.log('SignalR: Providing access token');
          //   return accessToken;
          // },
        })
        .withAutomaticReconnect([0, 2000, 5000, 10000, 30000])
        .configureLogging(signalR.LogLevel.Information)
        .build();

      // Set up event handlers
      this.setupConnectionHandlers();
      this.setupNotificationHandler();
      this.setupProgressUpdateHandler();

      // Start connection
      await this.connection.start();
      log.info('SignalR Connected successfully');
      this.reconnectAttempts = 0;
    } catch (error) {
      log.error('SignalR Connection Error:', error);
      this.handleConnectionError(error);
    } finally {
      this.isConnecting = false;
    }
  }

  /**
   * Disconnect from SignalR hub
   */
  public async disconnect(): Promise<void> {
    if (this.connection) {
      try {
        await this.connection.stop();
        log.debug('SignalR Disconnected');
      } catch (error) {
        log.error('Error disconnecting SignalR:', error);
      }
    }
    this.connection = null;
    this.currentToken = null; // Clear token on disconnect
    // Note: Don't clear handlers - they should persist across reconnections
    // this.handlers.clear();
    // this.progressHandlers.clear();
  }

  /**
   * Register a notification handler
   */
  public onNotification(handler: NotificationHandler): () => void {
    this.handlers.add(handler);
    // Return unsubscribe function
    return () => {
      this.handlers.delete(handler);
    };
  }

  /**
   * Register a progress update handler
   */
  public onProgressUpdate(handler: ProgressUpdateHandler): () => void {
    this.progressHandlers.add(handler);
    // Return unsubscribe function
    return () => {
      this.progressHandlers.delete(handler);
    };
  }

  /**
   * Register a custom event handler for any SignalR event
   * This allows components to listen for domain-specific events without modifying this service
   *
   * @param eventName - The name of the SignalR event to listen for
   * @param handler - Callback function that receives the event data
   * @returns Unsubscribe function to remove the handler
   *
   * @example
   * ```ts
   * const unsubscribe = signalRService.on('esignature:signer-signed', (data) => {
   *   console.log('Signer signed:', data);
   * });
   * // Later, to cleanup:
   * unsubscribe();
   * ```
   */
  public on<T = unknown>(eventName: string, handler: (data: T) => void): () => void {
    if (!this.connection) {
      log.warn(`Cannot register handler for "${eventName}" - no active connection`);
      // Return a no-op unsubscribe function
      return () => {};
    }

    log.debug(`Registering custom event handler for "${eventName}"`);

    // Register the handler on the connection
    this.connection.on(eventName, handler);

    // Return unsubscribe function
    return () => {
      log.debug(`Unregistering custom event handler for "${eventName}"`);
      this.connection?.off(eventName, handler);
    };
  }

  /**
   * Get connection state
   */
  public getConnectionState(): signalR.HubConnectionState | null {
    return this.connection?.state || null;
  }

  /**
   * Check if connected
   */
  public isConnected(): boolean {
    return this.connection?.state === signalR.HubConnectionState.Connected;
  }

  /**
   * Setup connection event handlers
   */
  private setupConnectionHandlers(): void {
    if (!this.connection) return;

    this.connection.onreconnecting((error) => {
      log.warn('SignalR reconnecting...', error);
      this.reconnectAttempts++;

      // Check if reconnection is due to auth error - if so, stop built-in reconnect
      // and let SignalRContext handle reconnection with fresh token
      const errorMessage = error instanceof Error ? error.message : String(error || '');
      const isAuthError = errorMessage.includes('401') || errorMessage.includes('Unauthorized');

      if (isAuthError) {
        log.warn('Reconnection triggered by auth error - will request fresh token');
        // Stop the connection to prevent further reconnection attempts with stale token
        this.connection?.stop().catch(() => {
          // Ignore errors during stop - we're already handling the auth error
        });
        this.handleConnectionError(error);
      }
    });

    this.connection.onreconnected((connectionId) => {
      log.debug('SignalR reconnected:', connectionId);
      this.reconnectAttempts = 0;
    });

    this.connection.onclose((error) => {
      log.error('SignalR connection closed:', error);
      this.handleConnectionError(error);
    });
  }

  /**
   * Setup notification message handler
   */
  private setupNotificationHandler(): void {
    if (!this.connection) return;

    this.connection.on('ReceiveNotification', (notification: SignalRNotification) => {
      try {
        log.debug('SignalR notification received:', notification);
        // Notify all registered handlers
        this.handlers.forEach((handler) => {
          try {
            handler(notification);
          } catch (error) {
            log.error('Error in notification handler:', error);
          }
        });
      } catch (error) {
        log.error('Error processing notification:', error);
      }
    });

    log.debug('ReceiveNotification handler registered');
  }

  /**
   * Setup progress update handler (new backend format)
   */
  private setupProgressUpdateHandler(): void {
    if (!this.connection) return;

    this.connection.on('ReceiveProgressUpdate', (update: ProgressUpdateDto) => {
      try {
        log.debug('SignalR progress update received:', {
          operationId: update.operationId,
          operationType: update.operationType,
          progress: update.progress,
          currentStep: update.currentStep,
          currentStepNumber: update.currentStepNumber,
          totalSteps: update.totalSteps,
        });
        // Notify all registered progress handlers
        this.progressHandlers.forEach((handler) => {
          try {
            handler(update);
          } catch (error) {
            log.error('Error in progress update handler:', error);
          }
        });
      } catch (error) {
        log.error('Error processing progress update:', error);
      }
    });

    log.debug('ReceiveProgressUpdate handler registered');
  }

  /**
   * Handle connection errors
   */
  private handleConnectionError(error?: Error | unknown): void {
    // Check if error is a 401 Unauthorized (expired token)
    // The error message from SignalR contains "401" when auth fails
    const errorMessage = error instanceof Error ? error.message : String(error || '');
    const isAuthError =
      (error && typeof error === 'object' && 'statusCode' in error && error.statusCode === 401) ||
      errorMessage.includes('401') ||
      errorMessage.includes('Unauthorized');

    if (isAuthError) {
      log.warn('401 Unauthorized - Token expired, requesting fresh token for reconnection');
      // Dispatch event to request token refresh and reconnection
      // The SignalRContext will handle getting a fresh token and reconnecting
      window.dispatchEvent(new CustomEvent('signalr:token-expired'));
      return;
    }

    if (this.reconnectAttempts >= this.maxReconnectAttempts) {
      log.error('Max reconnection attempts reached. Please refresh the page.');
      // Optionally notify user through a global notification system
    }
  }

  /**
   * Update access token (useful when token is refreshed)
   * This method gracefully reconnects with a new token without losing handlers
   */
  public async updateToken(newToken: string): Promise<void> {
    log.debug('Updating token and reconnecting...');

    // Check if token actually changed
    if (this.currentToken === newToken) {
      log.debug('Token unchanged, skipping reconnection');
      return;
    }

    // Save current connection state
    const wasConnected = this.isConnected();

    if (wasConnected) {
      log.debug('Disconnecting existing connection...');
      await this.disconnect();
    }

    // Reconnect with new token
    log.debug('Reconnecting with new token...');
    await this.connect(newToken);
  }
}

// Export singleton instance
export const signalRService = SignalRService.getInstance();
